import React, { useState } from "react";
import { Button, Form, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import "../auth.css";

/* ─── Icons ─────────────────────────────────────────────── */
const IconCalendar = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconUser  = () => <svg className="auth-input-icon" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconMail  = () => <svg className="auth-input-icon" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconPhone = () => <svg className="auth-input-icon" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.64 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.12 4.19 2 2 0 0 1 5.08 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91A16 16 0 0 0 15 15.91l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 23 17z"/></svg>;
const IconLock  = () => <svg className="auth-input-icon" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;


const PASSWORD_RE = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]).{8,}$/;
const strengthColors = ["transparent","#f87171","#fb923c","#facc15","#4ade80","#22c55e"];
const strengthLabels = ["","Very weak","Weak","Fair","Good","Strong"];

function getStrength(pw) {
  return [pw.length >= 8, /[a-z]/.test(pw), /[A-Z]/.test(pw), /[0-9]/.test(pw), /[\W_]/.test(pw)].filter(Boolean).length;
}

function Register() {
  const [form]     = Form.useForm();
  const [loading, setLoading]   = useState(false);
  const [password, setPassword] = useState("");
  const dispatch   = useDispatch();
  const navigate   = useNavigate();

  const strength = getStrength(password);
  const hints = [
    { label: "8+ chars", ok: password.length >= 8 },
    { label: "A–Z",      ok: /[A-Z]/.test(password) },
    { label: "0–9",      ok: /[0-9]/.test(password) },
    { label: "!@#",      ok: /[\W_]/.test(password) },
  ];

const onFinish = async (values) => {
  if (!PASSWORD_RE.test(values.password)) {
    toast.error(
      "Password must contain uppercase, lowercase, number, special character and be at least 8 characters"
    );
    return;
  }

  setLoading(true);

  try {
    dispatch(showLoading());

    const response = await axios.post(
      "/api/user/register",
      values
    );

    dispatch(hideLoading());

    if (response.data.success) {
      toast.success(response.data.message);

      toast("Redirecting to OTP Verification…");

      setTimeout(() => navigate("/verify-otp"), 3000);
    } else {
      toast.error(response.data.message);
    }

  } catch (error) {

    dispatch(hideLoading());

    if (
      error.response &&
      error.response.data &&
      error.response.data.message
    ) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Something went wrong");
    }

  } finally {
    setLoading(false);
  }
};
  return (
    <div className="auth-page">
      <div className="auth-cloud auth-cloud-1" />
      <div className="auth-cloud auth-cloud-2" />
      <div className="auth-cloud auth-cloud-3" />

      <div className="auth-card">
        <div className="auth-icon-wrap">
          <IconCalendar />
        </div>

        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">
          Join the portal and manage your healthcare appointments with ease.
        </p>

        <Form form={form} layout="vertical" onFinish={onFinish} className="auth-form">
          {/* Full name */}
          <Form.Item name="name" rules={[{ required: true, message: "Name required" }]}>
            <div className="auth-input-wrap">
              <IconUser />
              <Input placeholder="Full Name" size="large" />
            </div>
          </Form.Item>

          {/* Email + Phone */}
          <div className="auth-row">
            <Form.Item name="email" rules={[{ required: true, type: "email", message: "Valid email required" }]}>
              <div className="auth-input-wrap">
                <IconMail />
                <Input placeholder="Email" size="large" />
              </div>
            </Form.Item>
         <Form.Item
  name="phone"
  rules={[
    { required: true, message: "Phone required" },
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();

        // Remove spaces
        const cleaned = value.replace(/\s+/g, "");

        // Reject commas or multiple numbers
        if (cleaned.includes(",")) {
          return Promise.reject(
            new Error("Only one phone number is allowed")
          );
        }

        // International phone validation
      const phoneRegex =
  /^(\+?[1-9]\d{7,14}|0\d{10})$/;

        if (!phoneRegex.test(cleaned)) {
          return Promise.reject(
            new Error("Enter a valid phone number")
          );
        }

        return Promise.resolve();
      },
    },
  ]}
>
  <div className="auth-input-wrap">
    <IconPhone />
    <Input
      placeholder="Phone"
      size="large"
      onChange={(e) => {
        // Remove commas automatically
        const cleaned = e.target.value.replace(/,/g, "");
        form.setFieldsValue({ phone: cleaned });
      }}
    />
  </div>
</Form.Item>
          </div>

          {/* Password + Confirm */}
          <div className="auth-row">
            <Form.Item name="password" rules={[{ required: true, message: "Password required" }]}>
              <div className="auth-input-wrap">
                <IconLock />
                <Input.Password
                  placeholder="Password"
                  size="large"
                  onChange={(e) => setPassword(e.target.value)}
                  iconRender={(v) => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                />
              </div>
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Required" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) return Promise.resolve();
                    return Promise.reject(new Error("Passwords don't match"));
                  },
                }),
              ]}
            >
              <div className="auth-input-wrap">
                <IconLock />
                <Input.Password
                  placeholder="Confirm"
                  size="large"
                  iconRender={(v) => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                />
              </div>
            </Form.Item>
          </div>

          {/* Strength indicator */}
          {password && (
            <div style={{ marginTop: -2, marginBottom: 4 }}>
              <div className="pw-strength-bar">
                <div className="pw-strength-fill" style={{ width: `${(strength / 5) * 100}%`, background: strengthColors[strength] }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                <div className="pw-hints">
                  {hints.map(({ label, ok }) => (
                    <span key={label} className={`pw-hint${ok ? " pass" : ""}`}>{ok ? "✓ " : ""}{label}</span>
                  ))}
                </div>
                <span style={{ fontSize: 11, color: strengthColors[strength], fontWeight: 600 }}>
                  {strengthLabels[strength]}
                </span>
              </div>
            </div>
          )}

          <Form.Item>
            <Button htmlType="submit" loading={loading} className="auth-btn">
              {loading ? "Creating account…" : "Get Started"}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-divider">
          <div className="auth-divider-line" />
          <span className="auth-divider-text">OR</span>
          <div className="auth-divider-line" />
        </div>
        <p className="auth-prompt">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default Register;